import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Select, DatePicker, Modal, Form,
  Input, InputNumber, message, Popconfirm, Segmented, Statistic, Row, Col, Alert,
} from 'antd';
import { CheckOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getInternWorkDays, createInternWorkDay, approveInternWorkDay,
  rejectInternWorkDay, deleteInternWorkDay,
  getInterns, getInternPayments, createInternPayment, deleteInternPayment,
} from '../api/interns';
import { getBranches } from '../api/branches';
import UserSearchSelect from '../components/common/UserSearchSelect';
import useServerTable from '../hooks/useServerTable';
import useAuthStore from '../store/authStore';
import { apiErrorMessage } from '../utils/apiError';
import { WORK_DAY_STATUS_TAG, WORK_DAY_STATUS_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../constants/internStatus';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// Ücret/bakiye/ödeme görebilen roller. Backend'de ayrı izinlerle
// (interns.payments.*) korunuyor — burası yalnızca görünüm; öğretmen bu
// sekmeyi hiç görmemeli çünkü ücret bilgisi ona kapalı.
const PAYMENT_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];
const SUPER_ROLES   = ['developer_admin', 'super_admin'];

const money = (v) =>
  v === null || v === undefined ? '—' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`;

// ------------------------------------------------------------------
// Gün Onayları — öğretmen ve üstü
// ------------------------------------------------------------------
const WorkDaysTab = ({ canSeeAmounts }) => {
  const { user } = useAuthStore();
  const isSuperRole = SUPER_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [status, setStatus] = useState('pending');
  const [branchId, setBranchId] = useState(null);
  const [range, setRange] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const table = useServerTable('intern-work-days', 20);

  useEffect(() => {
    if (isSuperRole) {
      getBranches({ per_page: 100 }).then((r) => setBranches(r.data.data)).catch(() => {});
    }
  }, [isSuperRole]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getInternWorkDays({
        ...table.params(),
        status: status || undefined,
        branch_id: branchId || undefined,
        date_from: range?.[0]?.format('YYYY-MM-DD'),
        date_to: range?.[1]?.format('YYYY-MM-DD'),
      });
      setRows(data.data);
      setTotal(data.total ?? 0);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Günler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [table.page, table.pageSize, table.sorter, status, branchId, range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleApprove = async (row) => {
    try {
      await approveInternWorkDay(row.id, {});
      message.success('Gün onaylandı.');
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Onaylanamadı.'));
    }
  };

  const handleReject = async (row) => {
    try {
      await rejectInternWorkDay(row.id, {});
      message.success('Gün reddedildi.');
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Reddedilemedi.'));
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteInternWorkDay(row.id);
      message.success('Kayıt silindi.');
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Silinemedi.'));
    }
  };

  const handleAdd = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      await createInternWorkDay({
        user_id: values.user_id,
        date: values.date.format('YYYY-MM-DD'),
        amount: values.amount,
        notes: values.notes,
      });
      message.success('Gün eklendi ve onaylandı.');
      setAddOpen(false);
      form.resetFields();
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Eklenemedi.'));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Stajyer',
      key: 'intern',
      render: (_, r) => (r.user ? `${r.user.name} ${r.user.surname}` : '—'),
    },
    { title: 'Tarih', dataIndex: 'date', key: 'date', render: (d) => dayjs(d).format('DD.MM.YYYY ddd') },
    { title: 'Şube', key: 'branch', render: (_, r) => r.branch?.name || '—' },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const t = WORK_DAY_STATUS_TAG[s] || {};
        return <Tag color={t.color}>{t.label || s}</Tag>;
      },
    },
    // Tutar kolonu öğretmene HİÇ gösterilmiyor; API zaten alanı göndermiyor
    // ama kolonu da koymuyoruz ki boş bir sütun kafa karıştırmasın.
    ...(canSeeAmounts ? [{ title: 'Tutar', dataIndex: 'amount', key: 'amount', render: money }] : []),
    {
      title: 'Onaylayan',
      key: 'approver',
      render: (_, r) => (r.approver ? `${r.approver.name} ${r.approver.surname}` : '—'),
    },
    { title: 'Not', key: 'notes', render: (_, r) => r.reject_reason || r.notes || '—' },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, r) => (
        <Space>
          {r.status !== 'approved' && (
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(r)}>
              Onayla
            </Button>
          )}
          {r.status !== 'rejected' && (
            <Popconfirm title="Bu günü reddetmek istiyor musunuz?" onConfirm={() => handleReject(r)} okText="Reddet" cancelText="Vazgeç" okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<CloseOutlined />} />
            </Popconfirm>
          )}
          {canSeeAmounts && (
            <Popconfirm title="Kaydı silmek istiyor musunuz?" onConfirm={() => handleDelete(r)} okText="Sil" cancelText="Vazgeç" okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Gün Onayları"
      extra={canSeeAmounts && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ date: dayjs() }); setAddOpen(true); }}>
          Gün Ekle
        </Button>
      )}
    >
      <div className="flex gap-3 mb-4 flex-wrap">
        <Select
          allowClear
          placeholder="Durum"
          style={{ width: 170 }}
          value={status || undefined}
          options={WORK_DAY_STATUS_OPTIONS}
          onChange={(v) => { setStatus(v || null); table.resetToFirstPage(); }}
        />
        {isSuperRole && (
          <Select
            allowClear
            placeholder="Şube"
            style={{ width: 180 }}
            value={branchId || undefined}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            onChange={(v) => { setBranchId(v || null); table.resetToFirstPage(); }}
          />
        )}
        <RangePicker
          format="DD.MM.YYYY"
          value={range}
          onChange={(v) => { setRange(v); table.resetToFirstPage(); }}
        />
      </div>

      <Table
        columns={columns.map((c) => ({ ...c, ...(['date', 'status', 'amount', 'intern'].includes(c.key) ? table.sortProps(c.key) : {}) }))}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        size="small"
        {...table.tableProps(total)}
      />

      <Modal
        title="Gün Ekle"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={handleAdd}
        confirmLoading={submitting}
        okText="Ekle ve Onayla"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Yönetici tarafından eklenen gün doğrudan onaylı kaydedilir."
          description="Tutar boş bırakılırsa stajyerin tanımlı günlük ücreti uygulanır."
        />
        <Form form={form} layout="vertical">
          <Form.Item name="user_id" label="Stajyer" rules={[{ required: true, message: 'Stajyer seçin.' }]}>
            <UserSearchSelect
              role="stajyer"
              placeholder="Stajyer ara (en az 2 harf)..."
              renderLabel={(u) => `${u.name} ${u.surname}`}
            />
          </Form.Item>
          <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Tarih seçin.' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="amount" label="Tutar (boş = tanımlı günlük ücret)">
            <InputNumber min={0} style={{ width: '100%' }} addonAfter="₺" />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ------------------------------------------------------------------
// Stajyerler & Bakiye — yalnızca şube müdürü ve üstü
// ------------------------------------------------------------------
const InternBalancesTab = () => {
  const { user } = useAuthStore();
  const isSuperRole = SUPER_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [detail, setDetail] = useState(null);      // seçili stajyer
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const table = useServerTable('interns-list', 20);

  useEffect(() => {
    if (isSuperRole) {
      getBranches({ per_page: 100 }).then((r) => setBranches(r.data.data)).catch(() => {});
    }
  }, [isSuperRole]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getInterns({ ...table.params(), branch_id: branchId || undefined });
      setRows(data.data);
      setTotal(data.total ?? 0);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Stajyerler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [table.page, table.pageSize, table.sorter, branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openDetail = async (intern) => {
    setDetail(intern);
    try {
      const { data } = await getInternPayments(intern.id, { per_page: 50 });
      setPayments(data.data);
      setSummary(data.summary);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Ödemeler yüklenemedi.'));
    }
  };

  const handlePay = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await createInternPayment(detail.id, {
        amount: values.amount,
        paid_at: values.paid_at.format('YYYY-MM-DD'),
        payment_method: values.payment_method,
        notes: values.notes,
      });
      message.success('Ödeme kaydedildi.');
      setSummary(data.summary);
      setPayOpen(false);
      form.resetFields();
      openDetail(detail);
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Ödeme kaydedilemedi.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (id) => {
    try {
      const { data } = await deleteInternPayment(id);
      message.success('Ödeme silindi.');
      setSummary(data.summary);
      openDetail(detail);
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Silinemedi.'));
    }
  };

  const columns = [
    { title: 'Ad Soyad', key: 'name', render: (_, r) => `${r.name} ${r.surname}` },
    { title: 'Şube', key: 'branch', render: (_, r) => r.branch?.name || '—' },
    { title: 'Günlük Ücret', dataIndex: 'daily_wage', key: 'daily_wage', render: money },
    { title: 'Onaylı Gün', key: 'approved_days', render: (_, r) => r.summary?.approved_days ?? 0 },
    {
      title: 'Bekleyen',
      key: 'pending_days',
      render: (_, r) => (r.summary?.pending_days ? <Tag color="orange">{r.summary.pending_days}</Tag> : '—'),
    },
    { title: 'Hak Ediş', key: 'earned', render: (_, r) => money(r.summary?.earned) },
    { title: 'Ödenen', key: 'paid', render: (_, r) => money(r.summary?.paid) },
    {
      title: 'Kalan Alacak',
      key: 'balance',
      render: (_, r) => (
        <strong style={{ color: (r.summary?.balance ?? 0) > 0 ? '#cf1322' : '#3f8600' }}>
          {money(r.summary?.balance)}
        </strong>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, r) => (
        <Button size="small" icon={<DollarOutlined />} onClick={() => openDetail(r)}>
          Ödemeler
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card title="Stajyerler ve Bakiye">
        {isSuperRole && (
          <div className="mb-4">
            <Select
              allowClear
              placeholder="Şube filtrele"
              style={{ width: 200 }}
              value={branchId || undefined}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
              onChange={(v) => { setBranchId(v || null); table.resetToFirstPage(); }}
            />
          </div>
        )}
        <Table
          columns={columns.map((c) => ({ ...c, ...(['name', 'daily_wage'].includes(c.key) ? table.sortProps(c.key) : {}) }))}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          size="small"
          {...table.tableProps(total)}
        />
      </Card>

      <Modal
        title={detail ? `${detail.name} ${detail.surname} — Ödemeler` : ''}
        open={!!detail}
        onCancel={() => { setDetail(null); setPayments([]); setSummary(null); }}
        footer={null}
        width={800}
        destroyOnHidden
      >
        {summary && (
          <Row gutter={16} className="mb-4">
            <Col span={8}><Statistic title="Hak Ediş" value={summary.earned} precision={2} suffix="₺" /></Col>
            <Col span={8}><Statistic title="Ödenen" value={summary.paid} precision={2} suffix="₺" /></Col>
            <Col span={8}>
              <Statistic
                title="Kalan Alacak"
                value={summary.balance}
                precision={2}
                suffix="₺"
                valueStyle={{ color: summary.balance > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Col>
          </Row>
        )}

        <Button type="primary" icon={<PlusOutlined />} className="mb-3" onClick={() => {
          form.resetFields();
          form.setFieldsValue({ paid_at: dayjs(), payment_method: 'cash', amount: summary?.balance > 0 ? summary.balance : undefined });
          setPayOpen(true);
        }}>
          Ödeme Kaydet
        </Button>

        <Table
          dataSource={payments}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: 'Henüz ödeme kaydı yok.' }}
          columns={[
            { title: 'Tarih', dataIndex: 'paid_at', key: 'paid_at', render: (d) => dayjs(d).format('DD.MM.YYYY') },
            { title: 'Tutar', dataIndex: 'amount', key: 'amount', render: money },
            {
              title: 'Yöntem',
              dataIndex: 'payment_method',
              key: 'payment_method',
              render: (m) => PAYMENT_METHOD_OPTIONS.find((o) => o.value === m)?.label || m,
            },
            { title: 'Kaydeden', key: 'receiver', render: (_, r) => (r.receiver ? `${r.receiver.name} ${r.receiver.surname}` : '—') },
            { title: 'Not', dataIndex: 'notes', key: 'notes', render: (n) => n || '—' },
            {
              title: '',
              key: 'actions',
              render: (_, r) => (
                <Popconfirm title="Ödemeyi silmek istiyor musunuz?" onConfirm={() => handleDeletePayment(r.id)} okText="Sil" cancelText="Vazgeç" okButtonProps={{ danger: true }}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title="Ödeme Kaydet"
        open={payOpen}
        onCancel={() => setPayOpen(false)}
        onOk={handlePay}
        confirmLoading={submitting}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="amount" label="Tutar" rules={[{ required: true, message: 'Tutar girin.' }]}>
            <InputNumber min={0.01} style={{ width: '100%' }} addonAfter="₺" />
          </Form.Item>
          <Form.Item name="paid_at" label="Ödeme Tarihi" rules={[{ required: true, message: 'Tarih seçin.' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="payment_method" label="Yöntem">
            <Select options={PAYMENT_METHOD_OPTIONS} />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ------------------------------------------------------------------

const Interns = () => {
  const { user } = useAuthStore();
  const canSeeAmounts = PAYMENT_ROLES.includes(user?.role);
  const [tab, setTab] = useState('days');

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Stajyer Takibi</h2>

      {/* Öğretmene tek sekme kalıyor — ücret/bakiye ona kapalı. */}
      {canSeeAmounts && (
        <Segmented
          className="mb-4"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'days', label: 'Gün Onayları' },
            { value: 'balances', label: 'Stajyerler ve Bakiye' },
          ]}
        />
      )}

      {tab === 'days' || !canSeeAmounts
        ? <WorkDaysTab canSeeAmounts={canSeeAmounts} />
        : <InternBalancesTab />}
    </div>
  );
};

export default Interns;
